import React from 'react';
import { Copy, Loader, Plus, Star } from 'react-feather';
import { useLocation } from 'react-router-dom';

import { sanitizePriceInput } from '@/shared/utils/priceUtils';
import { useProductOptions } from '../../hooks/useProductOptions';

export interface ProductOptionsProps {
  productId?: string;
}

const ProductOptions: React.FC<ProductOptionsProps> = ({ productId }) => {
  const location = useLocation();
  const isCreateMode = location.pathname.includes('create');

  const {
    productOptionList,
    selectedOption,
    selectedOptionPrices,
    optionInputs,
    isSaving,
    isAddMode,
    recentlyAddedOptions,
    handleOptionSelect,
    handleOptionDelete,
    handleOptionInputChange,
    handlePriceTableChange,
    handlePriceApply,
    handleOptionSave,
    handleAddNewOption,
    handleEditOption,
    handleCopyOption,
    formatPrice,
  } = useProductOptions();

  const handleSupplyPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizePriceInput(e.target.value, 'KRW');
    handleOptionInputChange('supplyPrice', sanitized);
  };

  const handleProfitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizePriceInput(e.target.value, 'KRW');
    handleOptionInputChange('profit', sanitized);
  };

  const handleKRPriceChange = (
    field: 'supplyPrice' | 'profitPrice' | 'comparePrice',
    value: string,
  ) => {
    const sanitized = sanitizePriceInput(value, 'KRW');
    handlePriceTableChange('KR', field, sanitized);
  };

  const handleUSPriceChange = (
    field: 'supplyPrice' | 'profitPrice' | 'comparePrice',
    value: string,
  ) => {
    const sanitized = sanitizePriceInput(value, 'USD');
    handlePriceTableChange('US', field, sanitized);
  };

  return (
    <div className="flex flex-col rounded border border-[#F1F1F1] bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-5">
        <h2 className="text-base font-medium">Options</h2>
        {!isCreateMode && productOptionList.length > 0 && (
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded bg-[#95699F] px-3 py-1.5 text-sm text-white hover:bg-[#B995BE] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleAddNewOption}
            disabled={isSaving}
          >
            <Plus size={14} />
            Add New Option
          </button>
        )}
      </div>

      {productOptionList.length > 0 && (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[920px] table-fixed border-collapse">
            <thead>
              <tr className="border-y border-[#CECECE] bg-[#EFEFEF] text-left align-middle text-sm font-medium text-[#222]">
                <th className="w-10 px-2" />
                <th className="w-[13.75rem] px-2 py-1">Option name</th>
                <th className="w-[13.75rem] px-2 py-1">Option value</th>
                <th className="w-[13.75rem] px-2 py-1">Option name2</th>
                <th className="w-[13.75rem] px-2 py-1">Option value2</th>
                <th className="w-[13.75rem] px-2 py-1">Price</th>
                <th className="w-[13.75rem] px-2 py-1">Weight</th>
                <th className="w-[13.75rem] px-2 py-1">Barcode</th>
                <th className="w-[13.75rem] px-2 py-1">Stock</th>
                <th className="w-[13.75rem] px-2 py-1">
                  Minimum Order Quantity
                </th>
              </tr>
            </thead>
            <tbody>
              {productOptionList.map((option) => {
                const krPrice = option.productPriceList.find(
                  (price) => price.nationCode2 === 'KR',
                );
                const isRecentlyAdded = recentlyAddedOptions.includes(
                  option.optionUuid,
                );

                return (
                  <tr
                    key={option.optionUuid}
                    className={`border-b border-[#CECECE] ${
                      isRecentlyAdded ? 'bg-green-50' : ''
                    } ${option.isDefault ? 'bg-blue-50' : ''}`}
                  >
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      <div className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="selectedOption"
                          className="h-4 w-4 accent-[#95699F]"
                          checked={
                            selectedOption?.optionUuid === option.optionUuid
                          }
                          onChange={() => handleEditOption(option)}
                        />
                        {option.isDefault && (
                          <Star
                            size={12}
                            className="fill-yellow-500 text-yellow-500"
                          />
                        )}
                      </div>
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      <div className="flex items-center gap-2">
                        {option.optionNameKey1 || '-'}
                      </div>
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.optionNameValue1 || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.optionNameKey2 || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.optionNameValue2 || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {formatPrice(krPrice?.totalPrice)}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.weight || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.barcode || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.stock || '-'}
                    </td>
                    <td className="h-10 px-2 align-middle text-base font-normal text-[#1D1D1D]">
                      {option.minimumOrderQuantity || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between border-b border-[#CECECE] bg-[#EFEFEF] p-4">
        <h2 className="text-base font-medium text-[#1D1D1D]">Option</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-pointer rounded bg-[#D52E2E] px-4 py-1.5 text-sm text-white hover:bg-[#B82222] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() =>
              selectedOption && handleOptionDelete(selectedOption.optionUuid)
            }
            disabled={!selectedOption || isAddMode || isSaving}
          >
            Delete Option
          </button>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded bg-[#6B7280] px-4 py-1.5 text-sm text-white hover:bg-[#4B5563] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() =>
              selectedOption && handleCopyOption(selectedOption.optionUuid)
            }
            disabled={!selectedOption || isSaving}
          >
            <Copy size={12} />
            Copy Option
          </button>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded bg-[#3E3E3E] px-4 py-1.5 text-sm text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleOptionSave}
            disabled={isSaving}
          >
            {isSaving && <Loader size={12} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-5 px-4 py-5">
        <div className="flex justify-between gap-8">
          <div className="w-full min-w-[200px]">
            <p className="text-sm font-medium">
              Option name <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              className="mt-3 h-10 w-full rounded border border-[#CECECE] px-3 outline-none"
              value={optionInputs.optionNameKey1}
              onChange={(e) =>
                handleOptionInputChange('optionNameKey1', e.target.value)
              }
              maxLength={100}
            />
          </div>
          <div className="w-full min-w-[200px]">
            <p className="text-sm font-medium">
              Option value <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              className="mt-3 h-10 w-full rounded border border-[#CECECE] px-3 outline-none"
              value={optionInputs.optionNameValue1}
              onChange={(e) =>
                handleOptionInputChange('optionNameValue1', e.target.value)
              }
              maxLength={100}
            />
          </div>
        </div>
        <div className="flex justify-between gap-8">
          <div className="w-full min-w-[200px]">
            <p className="text-sm font-medium">Option name2</p>
            <input
              type="text"
              className="mt-3 h-10 w-full rounded border border-[#CECECE] px-3 outline-none"
              value={optionInputs.optionNameKey2}
              onChange={(e) =>
                handleOptionInputChange('optionNameKey2', e.target.value)
              }
              maxLength={100}
            />
          </div>
          <div className="w-full min-w-[200px]">
            <p className="text-sm font-medium">Option value2</p>
            <input
              type="text"
              className="mt-3 h-10 w-full rounded border border-[#CECECE] px-3 outline-none"
              value={optionInputs.optionNameValue2}
              onChange={(e) =>
                handleOptionInputChange('optionNameValue2', e.target.value)
              }
              maxLength={100}
            />
          </div>
        </div>
      </div>

      <hr className="border border-[#DEDEDE]" />

      <div>
        <h2 className="px-4 py-5 text-base font-medium">Pricing</h2>
        <div className="flex items-end justify-between gap-4 p-5">
          <div className="flex-1">
            <p className="text-sm font-medium">
              supply price <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              className="mt-3 w-full rounded border border-[#CECECE] px-3 py-2 text-xs outline-none"
              value={optionInputs.supplyPrice}
              onChange={handleSupplyPriceChange}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">
              profit <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              className="mt-3 w-full rounded border border-[#CECECE] px-3 py-2 text-xs outline-none"
              value={optionInputs.profit}
              onChange={handleProfitChange}
            />
          </div>
          <button
            type="button"
            className="cursor-pointer rounded bg-[#3E3E3E] px-3 py-1 text-sm font-medium text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handlePriceApply}
            disabled={isSaving}
          >
            Apply
          </button>
        </div>
      </div>

      <table className="w-full">
        <thead>
          <tr className="border-y border-[#E2E2E2] bg-[#EFEFEF] text-center text-xs font-medium text-[#1D1D1D]">
            <th className="w-12 py-1">-</th>
            <th className="w-[72px] py-1">Price</th>
            <th className="w-[148px] py-1">Supply price</th>
            <th className="w-[148px] py-1">Profit</th>
            <th className="w-[72px] py-1">Margin(%)</th>
            <th className="w-[148px] py-1">Discounted price</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-[#E2E2E2] text-center text-xs font-medium text-[#1D1D1D]">
            <td className="px-3 py-1.5">KR</td>
            <td className="bg-[#EFEFEF] px-3 py-1.5">
              {formatPrice(selectedOptionPrices.kr?.totalPrice)}
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.kr?.supplyPrice || ''}
                onChange={(e) =>
                  handleKRPriceChange('supplyPrice', e.target.value)
                }
              />
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.kr?.profitPrice || ''}
                onChange={(e) =>
                  handleKRPriceChange('profitPrice', e.target.value)
                }
              />
            </td>
            <td className="bg-[#EFEFEF] px-3 py-1.5">
              {selectedOptionPrices.kr?.marginPercent
                ? `${selectedOptionPrices.kr.marginPercent}%`
                : '-'}
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.kr?.comparePrice || ''}
                onChange={(e) =>
                  handleKRPriceChange('comparePrice', e.target.value)
                }
              />
            </td>
          </tr>
          <tr className="border-b border-[#E2E2E2] text-center text-xs font-medium text-[#1D1D1D]">
            <td className="px-3 py-1.5">US</td>
            <td className="bg-[#EFEFEF] px-3 py-1.5">
              {formatPrice(selectedOptionPrices.us?.totalPrice)}
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.us?.supplyPrice || ''}
                onChange={(e) =>
                  handleUSPriceChange('supplyPrice', e.target.value)
                }
              />
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.us?.profitPrice || ''}
                onChange={(e) =>
                  handleUSPriceChange('profitPrice', e.target.value)
                }
              />
            </td>
            <td className="bg-[#EFEFEF] px-3 py-1.5">
              {selectedOptionPrices.us?.marginPercent
                ? `${selectedOptionPrices.us.marginPercent}%`
                : '-'}
            </td>
            <td className="px-1">
              <input
                type="text"
                className="w-full rounded border border-[#CECECE] px-2 py-1 text-center text-xs outline-none"
                value={selectedOptionPrices.us?.comparePrice || ''}
                onChange={(e) =>
                  handleUSPriceChange('comparePrice', e.target.value)
                }
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex flex-col gap-5 px-5 pb-5 pt-8">
        <div>
          <p className="text-sm font-medium">
            Weight <span className="text-red-500">*</span>
          </p>
          <div className="relative">
            <input
              type="number"
              placeholder="0"
              className="mt-3 h-10 w-[120px] rounded border border-[#CECECE] px-3 pr-7 text-sm font-normal text-[#1D1D1D] outline-none"
              value={optionInputs.weight}
              onChange={(e) =>
                handleOptionInputChange('weight', e.target.value)
              }
              min="0"
              max="999.99"
              step="0.01"
            />
            <span className="absolute left-[92px] top-6 text-sm text-[#888]">
              Kg
            </span>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium">Barcode (ISBN, UPC, GTIN, etc.)</p>
          <input
            type="text"
            className="mt-3 h-10 w-60 rounded border border-[#CECECE] px-3 outline-none"
            value={optionInputs.barcode}
            onChange={(e) => handleOptionInputChange('barcode', e.target.value)}
            maxLength={30}
          />
        </div>
        <div>
          <p className="text-sm font-medium">
            Stock <span className="text-red-500">*</span>
          </p>
          <input
            type="number"
            placeholder="0"
            className="mt-3 h-10 w-60 rounded border border-[#CECECE] px-3 outline-none"
            value={optionInputs.stock}
            onChange={(e) => handleOptionInputChange('stock', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <p className="text-sm font-medium">Minimum Order Quantity</p>
          <input
            type="number"
            placeholder="1"
            className="mt-3 h-10 w-60 rounded border border-[#CECECE] px-3 outline-none"
            value={optionInputs.minimumOrderQuantity}
            onChange={(e) =>
              handleOptionInputChange('minimumOrderQuantity', e.target.value)
            }
            min="1"
          />
        </div>
      </div>
    </div>
  );
};

export default ProductOptions;
